import React, { useMemo } from 'react';
import { Alert, Box, IconButton, Snackbar, Tooltip, CircularProgress } from '@mui/material';
import {
    DeleteForever as DeleteForeverIcon,
    RemoveRedEye as RemoveRedEyeIcon,
    Restore as RestoreIcon,
    Sync as SyncIson,
} from '@mui/icons-material';
import { MaterialReactTable, MRT_ShowHideColumnsButton, MRT_ToggleFiltersButton, type MRT_ColumnDef } from 'material-react-table';
import { MRT_Localization_JA } from 'material-react-table/locales/ja';

import Log from '@/Logger';
import { FeatureContext } from '../../FeatureContext';
import { handleClickPreview } from '../document-list/PdfList.hooks';
import { ItemNameCell, multiSelectionFilter } from '../util/ItemSerlectDoropDown/ItemNameFIlter';
import { splitDocumentPath, type SplitedObjectKey, splitObjectKey } from '../util/splitObjectKey';
import { useConfirmDialog } from '../util/useConfirmDialog';
import { permanentlyDeleteDocument, restoreDocument, sendDocument } from './api';

import { useS3FileList } from '@/hooks/useS3FileList';
import { useDeviceDisplayName } from '@/hooks/useDeviceDisplayName';

type Props = {
    path: string;
};

export type TrashedPdfRecord = {
    name: string;
    serialId: string;
    deviceDisplayName: string;
    lastModified: string;
    size?: number;
    preview?: any;
    restore?: any;
    permanentlyDelete?: any;
};

type ButtonStates = {
    [id: string]: boolean;
};

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

// ゴミ箱のカラム比率
const DOCUMENT_NAME_WIDTH_RATIO_MIN = 0.45;
const DOCUMENT_NAME_WIDTH_RATIO_MAX = 0.55;
const DEVICE_NAME_WIDTH_RATIO = 0.07;
const DELETE_WIDTH_RATIO = 0.07;
const SIZE_WIDTH_RATIO = 0.05;
const PREVIEW_WIDTH_RATIO = 0.05;
const RECOVER_WIDTH_RATIO = 0.05;
const DELETE_COMPLETELY_WIDTH_RATIO = 0.05;
// デフォルト幅の定数
const DEFAULT_DEVICE_NAME_WIDTH = 130;

function getPositiveNumber(val: any): number {
    if (!Number.isFinite(val)) return 0;
    if (val <= 0) return 0;

    return val;
}

function formatSize(originalSize?: number) {
    const size = getPositiveNumber(originalSize);
    const tooltip = `${size.toLocaleString(undefined, { maximumFractionDigits: 2 })} バイト`;

    let view: number;
    let unit: string;

    if (size === 0) {
        view = 0;
        unit = 'KB';
    } else if (size < KB) {
        view = 1;
        unit = 'KB';
    } else if (size < MB) {
        view = size / KB;
        unit = 'KB';
    } else if (size < GB) {
        view = size / MB;
        unit = 'MB';
    } else {
        view = size / GB;
        unit = 'GB';
    }

    return (
        <Tooltip title={tooltip} arrow>
            <span>{`${view.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${unit}`}</span>
        </Tooltip>
    );
}

const TrashedPdfList = (props: Props) => {
    const logger = Log.getLogger();
    const tableRef = React.useRef<HTMLDivElement>(null);

    // カスタムフックを使用してPDFデータを管理
    const { list, isFetching: isPdfRecordsFetching, isLoading: isPdfRecordsLoading, refetch: fetchPdfList } = useS3FileList('TrachedPdfRecord', props.path);
    // デバイス表示名を管理するカスタムフック
    const { getDisplayName, isLoading: isDeviceDisplayNameLoading } = useDeviceDisplayName();

    // 読み込み中状態を定義
    const isLoading = list === undefined || !tableRef.current || isPdfRecordsLoading || isDeviceDisplayNameLoading;

    // デバイス表示名を付与したデータを生成
    const data = useMemo<TrashedPdfRecord[]>(() => {
        return list?.map((record) => ({
            ...record,
            deviceDisplayName: getDisplayName(record.serialId),
        })) ?? [];
    }, [list, getDisplayName]);

    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarText, setSnackBarText] = React.useState('');
    const [snackBarType, setSnackBarType] = React.useState<any>(undefined);

    // プレビュー、復元、完全削除連打防止用ステート
    const [isActiveClickDebounce, setIsActiveClickDebounce] = React.useState<ButtonStates>({ reload: false });

    const [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }] = useConfirmDialog();
    const [confirmDialogTitle, setConfirmDialogTitle] = React.useState<string>('');

    const [selectedDeviceList, setSelectedDeviceList] = React.useState<string[]>([]);

    const handleSnackBarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    // ドキュメントに対する操作
    const handleClickDocumentButton = async (objectKey: string, id: string, callback: (objectKey: string) => Promise<void>) => {
        // 押されたボタンを非活性化
        setIsActiveClickDebounce(prev => ({ ...prev, [id]: true }));

        try {
            await callback(objectKey);
        } catch (e) {
            logger.ui.error('PdfList', 'handleClickWithDebounce', `Error: ${JSON.stringify(e)}`);
        }

        // ドキュメントリストを更新
        fetchPdfList();

        // 押されたボタンを活性化
        setIsActiveClickDebounce(prev => ({ ...prev, [id]: false }));
    };

    const handleClickPreviewDocument = async (objectKey: string): Promise<void> => {
        try {
            logger.ui.info('PdfList', 'preview', 'Start to preview document');
            await handleClickPreview(objectKey);
            logger.ui.info('PdfList', 'preview', 'Succeed to preview document');
        } catch (e) {
            logger.ui.error('PdfList', 'preview', 'Failed to preview document');
            throw e;
        }
    };

    const handleClickRestoreDocument = async (objectKey: string): Promise<void> => {
        const splitedObjectKey: SplitedObjectKey = splitObjectKey(objectKey);
        const deviceDisplayName: string = data.find(record => record.name === objectKey)?.deviceDisplayName ?? splitedObjectKey.serialId;
        setConfirmDialogTitle('ドキュメントをごみ箱から復元しますか？');
        setConfirmDialogMessage([`${deviceDisplayName} の ${splitedObjectKey.documentName} を復元します。`, '同じ名前のファイルが存在する場合は上書きされます。'], true);

        const userSelect = await openConfirmDialog();

        if (userSelect === 'confirm') {
            logger.ui.info('PdfList', 'restore', 'Confirm is selected');
            try {
                logger.ui.info('PdfList', 'restore', 'Start to restore document');
                await restoreDocument(splitedObjectKey.serialId, splitedObjectKey.documentPath);
                logger.ui.info('PdfList', 'restore', 'Succeed to restore document');
            } catch (e) {
                logger.ui.error('PdfList', 'restore', 'Failed to restore document');
                const err = e as { serialId: string; deviceDisplayName: string; documentName: string; err: Error };
                setSnackBarType('error');
                setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(err.documentName).documentName} の復元要求に失敗しました`);
                setSnackBarOpen(true);
                throw e;
            }
            try {
                logger.ui.info('PdfList', 'send', 'Start to send document');
                const resp = await sendDocument(splitedObjectKey.serialId, splitedObjectKey.documentPath);
                logger.ui.info('PdfList', 'send', 'Succeed to send document');
                setSnackBarType('success');
                setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(resp.documentName).documentName} の復元要求に成功しました`);
                setSnackBarOpen(true);
            } catch (e) {
                logger.ui.error('PdfList', 'send', 'Failed to send document');
                const err = e as { serialId: string; deviceDisplayName: string; documentName: string; err: Error };
                setSnackBarType('error');
                setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(err.documentName).documentName} の復元要求に失敗しました`);
                setSnackBarOpen(true);
                throw e;
            }
        } else {
            logger.ui.info('PdfList', 'restore', 'Cancel is selected');
        }
    };

    const handleClickPermanentlyDeleteDocument = async (objectKey: string): Promise<void> => {
        const splitedObjectKey: SplitedObjectKey = splitObjectKey(objectKey);
        const deviceDisplayName: string = data.find(record => record.name === objectKey)?.deviceDisplayName ?? splitedObjectKey.serialId;
        setConfirmDialogTitle('ドキュメントを完全に削除しますか？');
        setConfirmDialogMessage([`${deviceDisplayName} から ${splitedObjectKey.documentName} を完全に削除します。`, '削除されたドキュメントは元に戻せません。'], true);

        const userSelect = await openConfirmDialog();

        if (userSelect === 'confirm') {
            logger.ui.info('PdfList', 'permanently delete', 'Confirm is selected');
            try {
                logger.ui.info('PdfList', 'permanently delete', 'Start to permanently delete document');
                const resp = await permanentlyDeleteDocument(splitedObjectKey.serialId, splitedObjectKey.documentPath);
                logger.ui.info('PdfList', 'permanently delete', 'Succeed to permanently delete document');
                setSnackBarType('success');
                setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(resp.documentName).documentName} の削除要求に成功しました`);
                setSnackBarOpen(true);
            } catch (e) {
                logger.ui.error('PdfList', 'useEffect', 'Failed to permanently delete document');
                const err = e as { serialId: string; deviceDisplayName: string; documentName: string; err: Error };
                setSnackBarType('error');
                setSnackBarText(`${deviceDisplayName} に対する ${splitDocumentPath(err.documentName).documentName} の削除要求に失敗しました`);
                setSnackBarOpen(true);
                throw e;
            }
        } else {
            logger.ui.info('PdfList', 'permanently delete', 'Cancel is selected');
        }
    };

    const columns: MRT_ColumnDef<TrashedPdfRecord>[] = [
        {
            accessorKey: 'name',
            header: 'ファイル名',
            minSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DOCUMENT_NAME_WIDTH_RATIO_MIN,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DOCUMENT_NAME_WIDTH_RATIO_MAX,
            Cell: ({ cell }) => <span>{splitObjectKey(cell.getValue<string>()).documentName}</span>,
            sortingFn: (a, b) => {
                const aName = splitObjectKey(a.original.name).documentName;
                const bName = splitObjectKey(b.original.name).documentName;
                if (aName === bName) return 0;
                if (aName < bName) return -1;
                return 1;
            },
        },
        {
            accessorKey: 'deviceDisplayName',
            header: 'デバイス名',
            minSize: 130,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DEVICE_NAME_WIDTH_RATIO,
            // ItemNameCellによる省略表示とツールチップ機能
            Cell: ({ cell }) => (
                <ItemNameCell
                    itemName={cell.getValue<string>()}
                    width={tableRef.current?.clientWidth ? tableRef.current.clientWidth * DEVICE_NAME_WIDTH_RATIO : DEFAULT_DEVICE_NAME_WIDTH}
                />
            ),
            Filter: ({ column }) => {
                const deviceNames = Array.from(new Set<string>(
                    data.map((record) => record.deviceDisplayName)
                ));

                const width = tableRef.current?.clientWidth ? tableRef.current.clientWidth * DEVICE_NAME_WIDTH_RATIO : DEFAULT_DEVICE_NAME_WIDTH;
                return multiSelectionFilter<TrashedPdfRecord>(
                    column,
                    deviceNames,
                    selectedDeviceList,
                    setSelectedDeviceList,
                    width
                )
            },
            filterFn: (row, _columnIds, filterValue) => {
                return filterValue.includes(row.getValue('deviceDisplayName'));
            },
        },
        {
            accessorKey: 'lastModified',
            header: '削除日時',
            minSize: 148,
            maxSize: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DELETE_WIDTH_RATIO,
        },
        {
            accessorKey: 'size',
            header: 'サイズ',
            muiTableHeadCellProps: {
                align: 'right',
            },
            muiTableBodyCellProps: {
                align: 'right',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * SIZE_WIDTH_RATIO,
            Cell: ({ cell }) => formatSize(cell.getValue<number>()),
            sortingFn: (a, b) => (a.original.size ?? 0) - (b.original.size ?? 0),
        },
        {
            accessorKey: 'preview',
            header: 'プレビュー',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * PREVIEW_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton
                    name="preview"
                    edge="end"
                    onClick={event => {
                        logger.ui.info('PdfList', 'preview');
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = 'preview' + row.id;
                        handleClickDocumentButton(row.original.name, event.currentTarget.id as string, handleClickPreviewDocument);
                    }}
                    size="small"
                    disabled={isActiveClickDebounce['preview' + row.id]}
                >
                    <RemoveRedEyeIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
        {
            accessorKey: 'restore',
            header: '復元',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * RECOVER_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton
                    name="restore"
                    edge="end"
                    data-testid="restore-button"
                    onClick={event => {
                        logger.ui.info('PdfList', 'restore');
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = 'restore' + row.id;
                        handleClickDocumentButton(row.original.name, event.currentTarget.id as string, handleClickRestoreDocument);
                    }}
                    size="small"
                    disabled={isActiveClickDebounce['restore' + row.id]}
                >
                    <RestoreIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
        {
            accessorKey: 'permanentlyDelete',
            header: '完全に削除',
            enableColumnActions: false,
            enableSorting: false,
            muiTableHeadCellProps: {
                align: 'center',
            },
            muiTableBodyCellProps: {
                align: 'center',
            },
            size: tableRef.current?.clientWidth && tableRef.current?.clientWidth * DELETE_COMPLETELY_WIDTH_RATIO,
            Cell: ({ row }) => (
                <IconButton
                    name="permanentlyDelete"
                    edge="end"
                    data-testid="delete-button"
                    onClick={event => {
                        logger.ui.info('PdfList', 'permanently delete');
                        //クリック時に押下したボタンにIDを付与
                        event.currentTarget.id = 'permanentlyDelete' + row.id;
                        handleClickDocumentButton(row.original.name, event.currentTarget.id as string, handleClickPermanentlyDeleteDocument);
                    }}
                    size="small"
                    disabled={isActiveClickDebounce['permanentlyDelete' + row.id]}
                >
                    <DeleteForeverIcon fontSize="small" />
                </IconButton>
            ),
            enableColumnFilter: false,
        },
    ];

    const features = React.useContext(FeatureContext);
    if (!features.permanentlyDeleteDocument()) {
        void columns.pop();
    }

    return (
        <>
            <MaterialReactTable
                columns={columns}
                data={data}
                positionPagination={'top'}
                enablePagination={false}
                enableFullScreenToggle={false}
                enableStickyHeader
                enableDensityToggle={false}
                enableGlobalFilterModes
                initialState={{
                    density: 'compact',
                    sorting: [{
                        id: 'lastModified',
                        desc: true
                    }],
                    showGlobalFilter: true,
                    showColumnFilters: true,
                }}
                state={{ isLoading }}
                muiLinearProgressProps={{
                    color: 'primary',
                    sx: { height: '3px' },
                }}
                muiTableContainerProps={{ ref: tableRef, sx: { height: 'calc(100vh - 206px)', width: 'calc(100vw - 57px)' }, style: { overflowX: 'auto', overflowY: 'scroll' } }}
                muiTablePaperProps={{
                    elevation: 0,
                    sx: {
                        width: '100%',
                        height: '100%',
                    },
                }}
                muiTableBodyCellProps={{ style: { whiteSpace: 'normal' } }} // ファイル名が長い場合に横スクロールではなく改行で対応する
                enableBottomToolbar={false}
                localization={MRT_Localization_JA}
                renderToolbarInternalActions={({ table }) => (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                        }}
                    >
                        <Tooltip title="再読み込み" arrow>
                            <IconButton
                                name="reflesh"
                                data-testid="reflesh-button"
                                edge="end"
                                onClick={async () => {
                                    if (!isActiveClickDebounce.reload && !isPdfRecordsFetching) {
                                        logger.ui.info('PdfList', 'reflesh');
                                        setIsActiveClickDebounce(prev => ({ ...prev, reload: true }));
                                        fetchPdfList();
                                        // refetchは非同期なので、連打防止のため少し待ってから有効化
                                        setTimeout(() => setIsActiveClickDebounce(prev => ({ ...prev, reload: false })), 1000);
                                    }
                                }}
                                size="medium"
                                style={{ marginRight: '0px' }}
                                disabled={isActiveClickDebounce.reload || isPdfRecordsFetching}
                            >
                                {isPdfRecordsFetching ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <SyncIson fontSize='medium' />
                                )}
                            </IconButton>
                        </Tooltip>
                        <MRT_ToggleFiltersButton table={table} />
                        <MRT_ShowHideColumnsButton table={table} />
                    </Box>
                )}
            />
            <Snackbar open={snackBarOpen} onClose={handleSnackBarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} data-testid="snackbar">
                <Alert onClose={handleSnackBarClose} severity={snackBarType} sx={{ width: '100%' }}>
                    {snackBarText}
                </Alert>
            </Snackbar>
            <ConfirmDialog title={confirmDialogTitle} />
        </>
    );
};

export default TrashedPdfList;
